import {Badge, MultiSelect, Pagination, Select} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {DateInput} from '@mantine/dates';
import React, {useState} from 'react';
import type {
  NexusInFlightResponse} from 'src/api/ProactApiClient';
import {
  WorkflowStateEnum,
} from 'src/api/ProactApiClient';
import {WorkflowStateDataOptions} from 'src/shared/constants/columnFilterData';
import {formatWaitDurationHours} from 'src/app/workflows/workflowDesignSystem';
import {getWorkflowStateColor} from 'src/app/workflows/workflowHelpers';
import {useGetNexusInFlightQuery} from 'src/redux/queries/workflowNexus';
import {useGetWorkflowDefinitionListQuery} from 'src/redux/queries/workflowDefinition';

const DEFAULT_STATES = [
  String(WorkflowStateEnum.Pending),
  String(WorkflowStateEnum.Waiting),
  String(WorkflowStateEnum.Active),
];

interface InFlightWorkflowsProps {
  selectedWorkflowId: number | null;
  onSelectWorkflow: (workflow: NexusInFlightResponse) => void;
}

function getBlockingDescription(item: NexusInFlightResponse): string {
  if (!item.blockingEntities?.length) return '';

  const entities = item.blockingEntities;

  // Schedule entity
  if (
    entities.length === 1 &&
    entities[0].entityType === 'schedule' &&
    entities[0].scheduleNextProcessDate
  ) {
    return `Next scheduled: ${new Date(
      entities[0].scheduleNextProcessDate
    ).toLocaleDateString()}`;
  }

  // Single task
  if (entities.length === 1 && entities[0].entityType === 'task') {
    const parts = [entities[0].name];
    if (entities[0].assigneeName)
      parts.push(`assigned to ${entities[0].assigneeName}`);
    if (entities[0].state) parts.push(`(${entities[0].state})`);
    return `Waiting on: ${parts.join(' — ')}`;
  }

  // Multiple tasks
  const suffix = item.mutuallyExclusive ? ' (any completes phase)' : '';
  return `Waiting on ${entities.length} tasks${suffix}`;
}

function getWaitBadgeColor(hours: number | undefined): string | null {
  if (!hours) return null;
  if (hours > 72) return 'red';
  if (hours >= 24) return 'yellow';
  return null;
}

export const InFlightWorkflows: React.FC<InFlightWorkflowsProps> = ({
  selectedWorkflowId,
  onSelectWorkflow,
}) => {
  const [page, setPage] = useState(0);
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<
    string | null
  >(null);
  const [createdFrom, setCreatedFrom] = useState<Date | null>(null);
  const [createdTo, setCreatedTo] = useState<Date | null>(null);
  const [selectedStates, setSelectedStates] = useState<string[]>(DEFAULT_STATES);
  const pageSize = 10;

  const {data: definitionsData} = useGetWorkflowDefinitionListQuery({
    start: 0,
    length: 100,
  });

  const definitions = (definitionsData?.data ?? []).map(d => ({
    value: String(d.id),
    label: d.name,
  }));

  const {
    data: response,
    isLoading,
    isFetching,
  } = useGetNexusInFlightQuery({
    start: page * pageSize,
    length: pageSize,
    workflowDefinitionId: selectedDefinitionId
      ? Number(selectedDefinitionId)
      : undefined,
    createdFrom: createdFrom ?? undefined,
    createdTo: createdTo ?? undefined,
    states:
      selectedStates.length > 0 ? selectedStates.map(Number) : undefined,
  });

  const data = response?.data ?? [];
  const totalRecords = response?.totalRecords ?? 0;

  if (isLoading) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (
    data.length === 0 &&
    !selectedDefinitionId &&
    !createdFrom &&
    !createdTo &&
    selectedStates.length === DEFAULT_STATES.length &&
    DEFAULT_STATES.every(s => selectedStates.includes(s))
  ) {
    return (
      <Box px="md" py="xl">
        <Text size="sm" c="dimmed" ta="center">
          No workflows currently in progress. Search for an entity above to
          explore its causal graph.
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap="md" px="md" py="sm">
      <Group gap="xs" grow>
        {definitions.length > 0 && (
          <Select
            label="Definition"
            placeholder="Filter by definition"
            data={definitions}
            value={selectedDefinitionId}
            onChange={val => {
              setSelectedDefinitionId(val);
              setPage(0);
            }}
            clearable
            size="xs"
          />
        )}
        <MultiSelect
          label="Status"
          data={WorkflowStateDataOptions}
          value={selectedStates}
          onChange={val => {
            setSelectedStates(val);
            setPage(0);
          }}
          size="xs"
        />
      </Group>
      <Group gap="xs" grow>
        <DateInput
          label="From"
          placeholder="From date"
          value={createdFrom}
          onChange={val => {
            setCreatedFrom(val);
            setPage(0);
          }}
          clearable
          size="xs"
        />
        <DateInput
          label="To"
          placeholder="To date"
          value={createdTo}
          onChange={val => {
            setCreatedTo(val);
            setPage(0);
          }}
          clearable
          size="xs"
        />
      </Group>
      {isFetching && (
        <Center py="xs">
          <Loader size="xs" />
        </Center>
      )}
      {data.length === 0 && !isFetching ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No workflows match the current filter.
        </Text>
      ) : (
        data.map(item => {
          const isSelected = item.workflowId === selectedWorkflowId;
          const stateStr = WorkflowStateEnum[item.state] ?? String(item.state);
          const waitText = formatWaitDurationHours(item.waitDurationHours);
          const waitColor = getWaitBadgeColor(item.waitDurationHours);
          const blockingDesc = getBlockingDescription(item);

          return (
            <Box
              key={item.workflowId}
              onClick={() => onSelectWorkflow(item)}
              px="sm"
              py="xs"
              style={{
                cursor: 'pointer',
                borderRadius: 'var(--mantine-radius-sm)',
                border: isSelected
                  ? '1px solid var(--mantine-color-blue-4)'
                  : '1px solid var(--mantine-color-gray-2)',
                backgroundColor: isSelected
                  ? 'var(--mantine-color-blue-0)'
                  : undefined,
              }}>
              <Group gap="xs" justify="space-between" wrap="nowrap">
                <Box style={{minWidth: 0, flex: 1}}>
                  <Group gap="xs" wrap="nowrap">
                    <Text size="sm" fw={500} truncate>
                      {item.definitionName ?? 'Unnamed'}
                    </Text>
                    {item.projectName && (
                      <Text size="xs" c="dimmed" truncate>
                        {item.projectName}
                      </Text>
                    )}
                    {item.createdAt && (
                      <Text size="xs" c="dimmed">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </Text>
                    )}
                  </Group>
                  <Group gap="xs" mt={2}>
                    <Badge
                      size="xs"
                      variant="light"
                      color={getWorkflowStateColor(stateStr)}>
                      {stateStr}
                    </Badge>
                    {item.currentPhaseName && (
                      <Text size="xs" c="dimmed">
                        {item.currentPhaseName}
                      </Text>
                    )}
                  </Group>
                  {blockingDesc && (
                    <Text size="xs" c="dimmed" mt={2} truncate>
                      {blockingDesc}
                    </Text>
                  )}
                </Box>
                {waitText &&
                  (waitColor ? (
                    <Badge size="xs" variant="light" color={waitColor}>
                      {waitText}
                    </Badge>
                  ) : (
                    <Text size="xs" c="dimmed">
                      {waitText}
                    </Text>
                  ))}
              </Group>
            </Box>
          );
        })
      )}
      {totalRecords > pageSize && (
        <Pagination
          total={Math.ceil(totalRecords / pageSize)}
          value={page + 1}
          onChange={p => setPage(p - 1)}
          size="sm"
          mt="xs"
        />
      )}
    </Stack>
  );
};
